import React from 'react'
import { Icon } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import './style.less'

/**
 * RadioSelectionInput - A reusable radio button selection component with card-style options.
 *
 * Props:
 *   name        - (string) HTML radio group name
 *   value       - (any)    Currently selected value
 *   onChange    - (func)   Callback when selection changes: (value) => void
 *   options     - (array)  Array of option objects:
 *       {
 *         value:       (any)    Option value
 *         label:       (string) Main label text
 *         description: (string) Optional description text below the label
 *         icon:        (string) Optional Semantic UI icon name
 *         iconColor:   (string) Optional Semantic UI icon color
 *         disabled:    (bool)   Whether this option is disabled
 *       }
 */
const RadioSelectionInput = ({ name, value, onChange, options }) => {
    return (
        <div className="radio-selection-input" role="radiogroup">
            {options.map((option) => {
                const isSelected = value === option.value
                const isDisabled = !!option.disabled

                return (
                    <div
                        key={option.value}
                        className={`radio-selection-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                        role="radio"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-checked={isSelected}
                        aria-disabled={isDisabled}
                        onClick={() => !isDisabled && onChange?.(option.value)}
                        onKeyDown={(event) => {
                            if (isDisabled) return
                            if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
                                event.preventDefault()
                                onChange?.(option.value)
                            }
                        }}
                    >
                        <label className="radio-selection-label">
                            <input
                                type="radio"
                                name={name}
                                checked={isSelected}
                                disabled={isDisabled}
                                onChange={() => !isDisabled && onChange?.(option.value)}
                                className="radio-selection-input-native"
                            />
                            {option.icon && (
                                <Icon name={option.icon} color={option.iconColor} style={{ margin: 0, lineHeight: 1 }} />
                            )}
                            <strong className="radio-selection-option-label">{option.label}</strong>
                        </label>
                        {option.description && (
                            <p className="radio-selection-description">
                                {option.description}
                            </p>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

RadioSelectionInput.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string,
        icon: PropTypes.string,
        iconColor: PropTypes.string,
        disabled: PropTypes.bool,
    })).isRequired,
}

export default RadioSelectionInput
